import Button from 'components/ui/Button';
import CssSphere from 'components/ui/CssSphere';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { b2cService } from 'services/b2cService';
import { getLanguage } from 'services/languageStore';
import { returnSelectors, returnThunks, setReturnSlip } from 'store/reducers/returnSlice';
import { styled } from 'styled-components';
import { B2CReturnState, ReturnB2CStatus, ReturnSlip } from 'returnCenter-api';
import { getB2CStatusColor, getB2CStatusKey, toReturnB2CStatus } from 'types/b2c';

/**
 * Where the e-commerce refund of a B2B2C return stands, per e-commerce order, with what the
 * e-commerce system reported. A completed return with configured order types that was never sent,
 * such as one completed before the config was added, can be sent from here, or marked as handled
 * outside the e-commerce system so it no longer needs attention. What can be done comes from the
 * server, which applies the same rules when it is done.
 */
const B2CPanel = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const returnId = returnSlip?.id;
    const b2cStatus = returnSlip?.b2CStatus ?? ReturnB2CStatus.None;

    const [state, setState] = useState<B2CReturnState | null>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [refreshCount, setRefreshCount] = useState(0);
    const [handledComment, setHandledComment] = useState<string | null>(null);

    // Also for returns never sent: whether one is something for the e-commerce system is the server's call.
    const load = useCallback(() => {
        if (!returnId) return;
        b2cService
            .getState(returnId)
            .then(setState)
            .catch(() => setState(null));
    }, [returnId, b2cStatus]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when the status moves

    // refreshCount reloads after a resend, also when the status stays the same.
    useEffect(load, [load, refreshCount]);

    const notifications = state?.notifications ?? [];
    const notSent = !!state?.notSent;

    if (!returnId || (b2cStatus === ReturnB2CStatus.None && !notSent)) return null;

    // Never on its way: sending is a first send, not a resend.
    const neverSent = notSent || (b2cStatus === ReturnB2CStatus.ManuallyHandled && notifications.length === 0);
    const canResend = !!state?.canSend;
    const canMarkHandled = !!state?.canMarkHandled;

    /** Runs a B2C action on the return and shows its result; the notifications reload afterwards. */
    const run = async (action: () => Promise<ReturnSlip>, successKey: string, errorKey: string) => {
        setIsBusy(true);
        try {
            dispatch(setReturnSlip(await action()));
            dispatch(returnThunks.getReturnLogs(returnId));
            // The badge on the Completed card; the status counts themselves do not change.
            dispatch(returnThunks.getB2CAttentionCount());
            toast.success(t(successKey), { autoClose: 3500, closeOnClick: true });
            return true;
        } catch {
            toast.error(t(errorKey), { autoClose: 3500, closeOnClick: true });
            return false;
        } finally {
            setIsBusy(false);
            // Picked up by the load effect, in the same render as the slip's new B2C status: one fetch.
            setRefreshCount((count) => count + 1);
        }
    };

    const handleResend = () =>
        run(
            () => b2cService.resend(returnId, getLanguage()),
            neverSent ? 'b2c.sent' : 'b2c.resent',
            'b2c.resendFailed',
        );

    const handleMarkHandled = async () => {
        const marked = await run(
            () => b2cService.markHandled(returnId, handledComment?.trim(), getLanguage()),
            'b2c.markedHandled',
            'b2c.markHandledFailed',
        );
        if (marked) setHandledComment(null);
    };

    const statusKey = notSent ? 'b2c.notSent' : getB2CStatusKey(b2cStatus);

    return (
        <Flex column className="b2c-wrapper">
            <Text as="label" xBold fontSize={11} className="mb-1" padding={0} margin={0}>
                {t('b2c.title')}
            </Text>
            <Wrapper column gap="0.5rem">
                <Flex gap="0.5rem" align="center">
                    <CssSphere size={8} color={getB2CStatusColor(b2cStatus)} />
                    <BolderText as="span">{statusKey ? t(statusKey) : ''}</BolderText>
                </Flex>
                {notifications.map((notification) => (
                    <Item key={notification.id} column gap="0.25rem">
                        <Flex gap="0.5rem" align="center">
                            <CssSphere size={6} color={getB2CStatusColor(toReturnB2CStatus(notification.status))} />
                            <BolderText as="span">
                                {t('b2c.order', {
                                    order: notification.customerOrderReference,
                                    system: notification.externalSystem,
                                })}
                            </BolderText>
                            <ItemStatus as="span">
                                {t(getB2CStatusKey(toReturnB2CStatus(notification.status)) ?? '')}
                            </ItemStatus>
                        </Flex>
                        {notification.errorMessage && (
                            <SmallText color="error" margin="0">
                                {notification.errorMessage}
                            </SmallText>
                        )}
                        {(notification.refunds ?? []).map((refund, index) => (
                            <SmallText key={index} margin="0">
                                {refund.sku} x{refund.quantity}
                                {refund.amount != null ? ` - ${refund.amount} ${refund.currency ?? ''}` : ''}
                            </SmallText>
                        ))}
                        {notification.paymentProvider && (
                            <SmallText margin="0">
                                {t('b2c.paymentProvider', { provider: notification.paymentProvider })}
                            </SmallText>
                        )}
                    </Item>
                ))}
                {canResend && (
                    <Button onClick={handleResend} disabled={isBusy} border rounded>
                        {t(neverSent ? 'b2c.send' : 'b2c.resend')}
                    </Button>
                )}
                {canMarkHandled &&
                    (handledComment === null ? (
                        <Button onClick={() => setHandledComment('')} disabled={isBusy} border rounded>
                            {t('b2c.markHandled')}
                        </Button>
                    ) : (
                        <Flex column gap="0.5rem">
                            <CommentInput
                                data-allow-paste
                                value={handledComment}
                                placeholder={t('b2c.markHandledComment')}
                                maxLength={500}
                                onChange={(e) => setHandledComment(e.target.value)}
                            />
                            <Flex gap="0.5rem">
                                <Button onClick={handleMarkHandled} disabled={isBusy} border rounded>
                                    {t('b2c.markHandled')}
                                </Button>
                                <Button onClick={() => setHandledComment(null)} disabled={isBusy} border rounded>
                                    {t('cancel')}
                                </Button>
                            </Flex>
                        </Flex>
                    ))}
            </Wrapper>
        </Flex>
    );
};

export default B2CPanel;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
`;

const CommentInput = styled.textarea`
    font-family: inherit;
    font-size: 12px;
    min-height: 60px;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    resize: vertical;
`;

const Item = styled(Flex)`
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight ?? '#eee'};
    padding-top: 0.5rem;
`;

const BolderText = styled(Text)`
    font-size: 12px;
    font-weight: 600;
`;

const ItemStatus = styled(Text)`
    color: ${({ theme }) => theme.colors.textFaded};
    font-size: 12px;
    font-weight: 400;
    margin: 0 0 0 auto;
    white-space: nowrap;
`;

const SmallText = styled(Text)`
    font-size: 12px;
    font-weight: 400;
`;
